import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { Button, Input, Checkbox, VStack, Box, Text, Link, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { FaUser, FaCalendarAlt, FaPhone, FaEnvelope } from 'react-icons/fa';
import API from '../api/axiosConfig';
import { WebAppContext } from '../contexts/WebAppContext';
import ErrorAlert from '../components/ErrorAlert';
import "../styles/components/LoginForm.css";

const LoginForm = () => {
    const navigate = useNavigate();
    const { webAppInfo, patientType, setPatientType } = useContext(WebAppContext);
    const [error, setError] = useState('');
    
    // Phone number formatting function
    const formatPhoneNumber = (value) => {
        const phoneNumber = value.replace(/\D/g, '');
        const limitedPhone = phoneNumber.slice(0, 11);
        
        if (limitedPhone.length === 0) {
            return '+1 ';
        } else if (limitedPhone.length <= 1) {
            return '+1 ';
        } else if (limitedPhone.length <= 4) {
            return `+1 (${limitedPhone.slice(1)}`;
        } else if (limitedPhone.length <= 7) {
            return `+1 (${limitedPhone.slice(1, 4)}) ${limitedPhone.slice(4)}`;
        } else {
            return `+1 (${limitedPhone.slice(1, 4)}) ${limitedPhone.slice(4, 7)}-${limitedPhone.slice(7, 11)}`;
        }
    };
    
    const PatientTypeCard = ({ type, label }) => (
        <Box
            className="patient-type-card"
            onClick={() => setPatientType(type)}
            role="button"
            aria-pressed={patientType === type ? "true" : "false"}
        >
            <Text className="patient-type-card-text">{label}</Text>
        </Box>
    );
    
    if (!patientType) {
        return (
            <Box className="welcome-section">
                <VStack className="welcome-content">
                    <Box className="welcome-text">
                        <h1 className="welcome-title">Welcome!</h1>
                        <p className="patient-prompt">
                            Please tell us if you're a new or returning patient:
                        </p>
                    </Box>
                    <Box className="patient-type-cards-container">
                        <PatientTypeCard type="new" label="New Patient" />
                        <PatientTypeCard type="existing" label="Returning Patient" />
                    </Box>
                </VStack>
            </Box>
        );
    }

    return (
        <Box className="login-form-wrapper">
            <Box className="login-form-container">
                {error && <ErrorAlert title="Form Submission Error" description={error} onClose={() => setError('')} />}
                
                <div className="icon-container">
                    {patientType === 'new' ? '✨' : '👋'}
                </div>
                
                <h1 className="login-title">
                    {patientType === 'new' ? 'Create Account' : 'Welcome Back'}
                </h1>
                
                <Text className="login-subtitle">
                    {patientType === 'new' ? (
                        <>
                            Please fill in your details to create your account. Already have an account?{' '}
                            <Link className="switch-link" onClick={() => setPatientType('existing')}>Sign in here</Link>
                        </>
                    ) : (
                        <>
                            Please sign in to continue. New patient?{' '}
                            <Link className="switch-link" onClick={() => setPatientType('new')}>Create account</Link>
                        </>
                    )}
                </Text>

                <Formik
                    initialValues={{
                        firstName: '', lastName: '', dob: '', phone: '+1 ', email: '', agreeToPrivacy: false
                    }}
                    validate={(values) => {
                        const errors = {};
                        if (!values.firstName) errors.firstName = 'First Name is required';
                        if (!values.lastName) errors.lastName = 'Last Name is required';
                        if (!values.dob) errors.dob = 'Date of Birth is required';
                        
                        const digits = values.phone.replace(/\D/g, '');
                        if (digits.length < 11) {
                            errors.phone = 'Phone number must be 10 digits';
                        }
                        
                        if (!values.email) errors.email = 'Email is required';
                        if (!values.agreeToPrivacy) errors.agreeToPrivacy = 'You must agree to the Terms and Privacy Policy';
                        return errors;
                    }}
                    onSubmit={(values, { setSubmitting }) => {
                        const tempToken = sessionStorage.getItem('tempToken');
                        if (!tempToken) {
                            setError('Temporary token not found.');
                            setSubmitting(false);
                            return;
                        }

                        const phoneDigits = values.phone.replace(/\D/g, '');

                        API.post('/web/patient/check', {
                            fname: values.firstName,
                            lname: values.lastName,
                            mobile: phoneDigits,
                            birthdate: values.dob,
                            returning: patientType === 'existing' ? 'true' : 'false'
                        }, {
                            headers: {
                                'Authorization': `Bearer ${tempToken}`,
                            }
                        })
                        .then(response => {
                            if (response.data.token) {
                                sessionStorage.setItem('otpToken', response.data.token);
                            }
                            setSubmitting(false);
                            navigate('/verify');
                        })
                        .catch(error => {
                            console.error('Error during form submission:', error);
                            setError(error.response?.data?.message || 'An unexpected error occurred');
                            setSubmitting(false);
                        });                    
                    }}
                >
                    {({ handleChange, handleSubmit, isSubmitting, errors, touched, setFieldValue, values }) => (
                        <Form onSubmit={handleSubmit}>
                            <VStack spacing={4} className="form-fields">
                                <Box className="form-field">
                                    <InputGroup>
                                        <InputLeftElement className="input-icon">
                                            <FaUser />
                                        </InputLeftElement>
                                        <Input 
                                            name="firstName" 
                                            onChange={handleChange} 
                                            placeholder="First Name" 
                                            aria-label="First Name"
                                            className="form-input"
                                        />
                                    </InputGroup>
                                    {errors.firstName && touched.firstName && (
                                        <Text className="error-text">{errors.firstName}</Text>
                                    )}
                                </Box>

                                <Box className="form-field">
                                    <InputGroup>
                                        <InputLeftElement className="input-icon">
                                            <FaUser />
                                        </InputLeftElement>
                                        <Input 
                                            name="lastName" 
                                            onChange={handleChange} 
                                            placeholder="Last Name" 
                                            aria-label="Last Name"
                                            className="form-input"
                                        />
                                    </InputGroup>
                                    {errors.lastName && touched.lastName && (
                                        <Text className="error-text">{errors.lastName}</Text>
                                    )}
                                </Box>

                                <Box className="form-field">
                                    <InputGroup>
                                        <InputLeftElement className="input-icon">
                                            <FaCalendarAlt />
                                        </InputLeftElement>
                                        <Input 
                                            name="dob" 
                                            type="date" 
                                            onChange={handleChange} 
                                            placeholder="Date of Birth" 
                                            aria-label="Date of Birth"
                                            className="form-input"
                                        />
                                    </InputGroup>
                                    {errors.dob && touched.dob && (
                                        <Text className="error-text">{errors.dob}</Text>
                                    )}
                                </Box>

                                <Box className="form-field">
                                    <InputGroup>
                                        <InputLeftElement className="input-icon">
                                            <FaPhone />
                                        </InputLeftElement>
                                        <Input 
                                            name="phone" 
                                            value={values.phone}
                                            onChange={(e) => {
                                                const formatted = formatPhoneNumber(e.target.value);
                                                setFieldValue('phone', formatted);
                                            }}
                                            placeholder="+1 (555) 555-5555" 
                                            aria-label="Phone Number"
                                            className="form-input"
                                        />
                                    </InputGroup>
                                    {errors.phone && touched.phone && (
                                        <Text className="error-text">{errors.phone}</Text>
                                    )}
                                </Box>

                                <Box className="form-field">
                                    <InputGroup>
                                        <InputLeftElement className="input-icon">
                                            <FaEnvelope />
                                        </InputLeftElement>
                                        <Input 
                                            name="email" 
                                            onChange={handleChange} 
                                            placeholder="Email" 
                                            aria-label="Email"
                                            className="form-input"
                                        />
                                    </InputGroup>
                                    {errors.email && touched.email && (
                                        <Text className="error-text">{errors.email}</Text>
                                    )}
                                </Box>

                                <Box className="form-field checkbox-field">
                                    <Checkbox 
                                        name="agreeToPrivacy" 
                                        onChange={handleChange} 
                                        aria-label="Agree to Privacy Terms" 
                                        className="privacy-checkbox"
                                    >
                                        <span className="checkbox-text">
                                            I agree to the{' '}
                                            <a 
                                                href={webAppInfo.legalInfo?.termsPrivacy.url || "#"} 
                                                className="privacy-link" 
                                                target="_blank" 
                                                rel="noreferrer"
                                            >
                                                {webAppInfo.legalInfo?.termsPrivacy.displayText || 'Terms & Privacy Policy'}
                                            </a>
                                        </span>
                                    </Checkbox>
                                    {errors.agreeToPrivacy && touched.agreeToPrivacy && (
                                        <Text className="error-text">{errors.agreeToPrivacy}</Text>
                                    )}
                                </Box>

                                <Button 
                                    type="submit" 
                                    isLoading={isSubmitting}
                                    loadingText="Processing"
                                    className="submit-btn"
                                    width="100%"
                                >
                                    {patientType === 'new' ? 'Create Account' : 'Sign In'}
                                </Button>
                            </VStack>
                        </Form>
                    )}
                </Formik>
            </Box>
        </Box>
    );
};

export default LoginForm;